import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Loading } from '@linktivity/link-ui';
import {
  AsyncState,
  PageLoading,
  SeatMapPanel,
  SeatPreferenceSelector,
  SelectField,
  StickyActionFooter
} from '@link/share/rail-booking';

import { useStore } from '@link/emot/stores';
import {
  SeatSelectionMode,
  type SeatPreference
} from '@link/emot/services/types';

import styles from './seatSelection.module.css';

const SINGLE_PASSENGER_PREFERENCE_CODES = ['0', '4', '6'];
const MULTI_PASSENGER_PREFERENCE_CODES = ['11', '12', '13'];

interface SeatPreferenceOption {
  code: string;
  name: string;
  source: SeatPreference;
}

const SeatSelectionView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { emot } = useStore();
  const { bookingInfo, orderParameters, selection, seatMap } = emot;
  const [orderParametersError, setOrderParametersError] = useState(false);
  const [orderParametersRetryKey, setOrderParametersRetryKey] = useState(0);
  const [seatMapLoading, setSeatMapLoading] = useState(false);
  const [seatMapError, setSeatMapError] = useState(false);
  const [seatMapRetryKey, setSeatMapRetryKey] = useState(0);

  const passengerCount = useMemo(
    () =>
      bookingInfo?.units?.reduce(
        (sum, unit) => sum + (unit.quantity ?? 0),
        0
      ) ?? 0,
    [bookingInfo?.units]
  );

  useEffect(() => {
    if (!selection.diagram || orderParameters) return;

    let active = true;
    setOrderParametersError(false);
    void emot.getOrderParameters().catch(() => {
      if (active) setOrderParametersError(true);
    });

    return () => {
      active = false;
    };
  }, [emot, orderParameters, orderParametersRetryKey, selection.diagram]);

  const carOptions = useMemo(() => {
    const options =
      selection.diagram?.carInfos
        ?.filter(car => car.carId)
        .map(car => ({ label: car.name ?? '', value: car.carId ?? '' })) ?? [];
    if (
      passengerCount === 1 &&
      selection.seatSelectionMode === SeatSelectionMode.Preference
    ) {
      return [
        { label: t('views.seat.noCarPreference'), value: '' },
        ...options
      ];
    }
    return options;
  }, [
    passengerCount,
    selection.diagram?.carInfos,
    selection.seatSelectionMode,
    t
  ]);

  const modeOptions = useMemo(
    () => [
      {
        code: SeatSelectionMode.Preference,
        name: t('views.seat.selectByPreference')
      },
      {
        code: SeatSelectionMode.SeatMap,
        name: t('views.seat.selectBySeatMap')
      }
    ],
    [t]
  );

  const visiblePreferences = useMemo(() => {
    const allowed =
      passengerCount > 1
        ? MULTI_PASSENGER_PREFERENCE_CODES
        : SINGLE_PASSENGER_PREFERENCE_CODES;
    const preferences = orderParameters?.seatPreferences ?? [];
    const filtered = preferences.filter(item =>
      allowed.includes(item.code ?? '')
    );
    return (filtered.length > 0 ? filtered : preferences)
      .filter(preference => preference.code)
      .map(
        (preference): SeatPreferenceOption => ({
          code: preference.code ?? '',
          name: preference.name ?? '',
          source: preference
        })
      );
  }, [orderParameters?.seatPreferences, passengerCount]);

  useEffect(() => {
    if (!orderParameters || selection.seatSelectionMode) return;
    emot.setSeatSelectionMode(SeatSelectionMode.Preference);
  }, [emot, orderParameters, selection.seatSelectionMode]);

  useEffect(() => {
    if (
      selection.seatSelectionMode !== SeatSelectionMode.Preference ||
      visiblePreferences.length === 0 ||
      visiblePreferences.some(
        preference => preference.code === selection.seatPreference?.code
      )
    ) {
      return;
    }
    emot.setSeatPreference(visiblePreferences[0].source);
  }, [
    emot,
    selection.seatPreference?.code,
    selection.seatSelectionMode,
    visiblePreferences
  ]);

  useEffect(() => {
    if (carOptions.length === 0) return;
    if (!carOptions.some(option => option.value === selection.carId)) {
      emot.setCarId(carOptions[0].value);
    }
  }, [carOptions, emot, selection.carId]);

  useEffect(() => {
    if (
      selection.seatSelectionMode !== SeatSelectionMode.SeatMap ||
      !selection.diagram ||
      !selection.seatClass?.seatClassId ||
      !selection.carId
    ) {
      setSeatMapLoading(false);
      return;
    }

    let active = true;
    setSeatMapError(false);
    setSeatMapLoading(true);
    void emot
      .getSeatMap()
      .catch(() => {
        if (active) setSeatMapError(true);
      })
      .finally(() => {
        if (active) setSeatMapLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    emot,
    seatMapRetryKey,
    selection.carId,
    selection.diagram,
    selection.seatClass?.seatClassId,
    selection.seatSelectionMode
  ]);

  if (!selection.diagram) {
    return (
      <AsyncState
        status="error"
        title={t('views.seat.noTrain')}
        message={t('views.seat.selectTrainFirst')}
        actionLabel={t('common.back')}
        onAction={() => navigate(-1)}
      />
    );
  }

  if (orderParametersError) {
    return (
      <AsyncState
        status="error"
        title={t('common.loadFailed')}
        actionLabel={t('common.retry')}
        onAction={() => setOrderParametersRetryKey(key => key + 1)}
      />
    );
  }

  if (!bookingInfo || !orderParameters) {
    return <PageLoading />;
  }

  const selectedSeatIds = selection.selectedSeatIds ?? [];
  const seatMapPending =
    seatMapLoading || (carOptions.length > 0 && !selection.carId);
  const canContinue =
    passengerCount > 0 &&
    (selection.seatSelectionMode === SeatSelectionMode.Preference
      ? Boolean(selection.seatPreference)
      : selection.seatSelectionMode === SeatSelectionMode.SeatMap &&
        selectedSeatIds.length === passengerCount);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>{t('views.seat.title')}</h1>
        <p className={styles.ticketCount}>
          {t('views.seat.ticketCount')}
          {bookingInfo.units
            ?.map(unit => `${unit.name ?? ''} ×${unit.quantity ?? 0}`)
            .join(' ')}
        </p>

        <SeatPreferenceSelector
          title={t('views.seat.selectMethod')}
          options={modeOptions}
          value={selection.seatSelectionMode}
          onChange={option => {
            if (option.code === SeatSelectionMode.SeatMap) {
              setSeatMapLoading(true);
            }
            emot.setSeatSelectionMode(option.code);
          }}
        />

        {selection.seatSelectionMode === SeatSelectionMode.Preference && (
          <SeatPreferenceSelector
            title={t('views.seat.preference')}
            options={visiblePreferences}
            value={selection.seatPreference?.code}
            onChange={preference => emot.setSeatPreference(preference.source)}
          />
        )}

        <SelectField
          label={t('views.seat.car')}
          value={selection.carId}
          options={carOptions}
          disabled={carOptions.length === 0}
          onChange={value => {
            if (selection.seatSelectionMode === SeatSelectionMode.SeatMap) {
              setSeatMapLoading(true);
            }
            emot.setCarId(value);
          }}
        />

        {selection.seatSelectionMode === SeatSelectionMode.SeatMap &&
          seatMapPending && (
            <div className={styles.seatMapLoading}>
              <Loading />
            </div>
          )}

        {selection.seatSelectionMode === SeatSelectionMode.SeatMap &&
          !seatMapPending && (
            <SeatMapPanel
              title={t('views.seat.selectSeats', {
                selected: selectedSeatIds.length,
                total: passengerCount
              })}
              seats={seatMap}
              selectedIds={selectedSeatIds}
              emptyMessage={t('views.seat.noSeats')}
              description={t('views.seat.seatMapDescription')}
              seatLabel={seat =>
                t('views.seat.seatLabel', {
                  seat: seat.showName ?? seat.id ?? ''
                })
              }
              onToggle={seat => {
                if (seat.id) emot.toggleSeat(seat.id, passengerCount);
              }}
              error={seatMapError}
              loadFailedTitle={t('common.loadFailed')}
              retryLabel={t('common.retry')}
              onRetry={() => setSeatMapRetryKey(key => key + 1)}
            />
          )}
      </div>

      <StickyActionFooter
        label={t('views.seat.next')}
        disabled={!canContinue}
        onClick={() => navigate('../confirm')}
      />
    </div>
  );
};

const SeatSelection = observer(SeatSelectionView);

export default SeatSelection;
